import type { PitchProofChoice } from 'fym-shared/src/music24/outreach/pitchProof';
import type { OutreachPitchKind } from 'fym-shared/src/music24/outreach/dailyPitches';
import { useTRPCClientAPI, useTRPCMutation } from '../../../../trpc/trpc';
import type { SamplePitch } from '../outreachTypes';
import type { PITCH_ANGLES } from '../../../../../../music24-api/src/outreach/writing/pitchPrompt';
import { useInvalidateOutreach } from './useInvalidateOutreach';

/** The angles that the API takes (`campaigns.update`). */
export type PitchAngle = (typeof PITCH_ANGLES)[number];

export type CampaignGoal = 'playlists' | 'press' | 'radio' | 'mixed';

export interface CampaignPatch {
  angle?: PitchAngle | null;
  userWords?: string | null;
  lockedLines?: string | null;
  /** 5, or null for no follow-up (the only values the API takes). */
  followUpDays?: 5 | null;
  /** v2 sends `weekdaysOnly` only; the server keeps the other fields. */
  schedule?: { weekdaysOnly: boolean };
  mailboxUuids?: string[];
  mode?: 'review' | 'autopilot';
  /** Another song: before the start only. */
  catalogItemUuid?: string;
  /** Stored only: it does not change `kinds` (send `kinds` too). */
  goal?: CampaignGoal;
  name?: string;
  /** The kinds of people that Plug pitches (step 2). At least one. */
  kinds?: OutreachPitchKind[];
  /** The fixed part of the artist's daily rate; null splits by release day. */
  dailyShare?: number | null;
  /** 4, 8 or 12 weeks after the release; null runs until the artist stops it. */
  endWeeks?: 4 | 8 | 12 | null;
  /** The proof that the artist chose (at most 3): data keys and one free line. */
  proof?: PitchProofChoice[];
}

/** A new draft (`campaigns.create`): the song and the goal. Nobody is picked; Plug picks every day. */
export interface CampaignCreateInput {
  catalogItemUuid: string;
  goal: CampaignGoal;
  name?: string;
}

/** `campaigns.start` (music24-api `StartCampaignResult`). */
export interface StartCampaignResult {
  status: 'sending' | 'scheduled';
  firstBatch: 'today' | 'tomorrow' | 'nextBatch';
  pickedToday: number;
}

/** The campaign writes of the builder, the results page and the home (frames e, f2, h). */
export function useCampaignActions(accountId: number) {
  const trpcClient = useTRPCClientAPI();
  const invalidate = useInvalidateOutreach();
  const done = () => invalidate(['campaigns', 'home', 'review']);

  const create = useTRPCMutation({
    mutationFn: (input: CampaignCreateInput) =>
      trpcClient.outreach.campaigns.create.mutate({ accountId, ...input }) as Promise<{ campaignUuid: string }>,
    onSuccess: done,
  });
  const update = useTRPCMutation({
    mutationFn: (input: CampaignPatch & { campaignUuid: string }) =>
      trpcClient.outreach.campaigns.update.mutate({ accountId, ...input }),
    onSuccess: done,
  });
  const preview = useTRPCMutation({
    mutationFn: (input: { campaignUuid: string; kind: OutreachPitchKind | null }) =>
      trpcClient.outreach.campaigns.previewMessage.mutate({ accountId, ...input }) as Promise<SamplePitch>,
  });
  const start = useTRPCMutation({
    mutationFn: (campaignUuid: string) =>
      trpcClient.outreach.campaigns.start.mutate({ accountId, campaignUuid }) as Promise<StartCampaignResult>,
    onSuccess: done,
  });
  const pause = useTRPCMutation({
    mutationFn: (campaignUuid: string) => trpcClient.outreach.campaigns.pause.mutate({ accountId, campaignUuid }),
    onSuccess: done,
  });
  const resume = useTRPCMutation({
    mutationFn: (campaignUuid: string) => trpcClient.outreach.campaigns.resume.mutate({ accountId, campaignUuid }),
    onSuccess: done,
  });
  const remove = useTRPCMutation({
    mutationFn: (campaignUuid: string) => trpcClient.outreach.campaigns.delete.mutate({ accountId, campaignUuid }),
    onSuccess: done,
  });

  return {
    createCampaign: create.mutateAsync,
    updateCampaign: update.mutateAsync,
    previewMessage: preview.mutateAsync,
    isPreviewing: preview.isPending,
    startCampaign: start.mutateAsync,
    pauseCampaign: pause.mutateAsync,
    resumeCampaign: resume.mutateAsync,
    deleteCampaign: remove.mutateAsync,
    isSaving: create.isPending || update.isPending,
    isChangingState: start.isPending || pause.isPending || resume.isPending || remove.isPending,
    error: create.error ?? update.error ?? start.error ?? null,
  };
}
